import {storeTusFileEntry} from '@app/gen/files';
import {parseApiError} from '@common/http/errors/parsed-api-error';
import {toast} from '@shadcn/toast/toast';
import {UploadedFile} from '@ui/utils/files/uploaded-file';
import {getCookie} from '@ui/utils/hooks/use-cookie';
import {Upload} from 'tus-js-client';
import {
  UploadStrategy,
  UploadStrategyConfigWithBackend,
} from './upload-strategy';

const resumeToasts = new Set<string>();

function toastResume(fileId: string, offset: number, size: number) {
  if (!fileId || resumeToasts.has(fileId) || !size || offset <= 0 || offset >= size) {
    return;
  }
  resumeToasts.add(fileId);
  const p = Math.min(99, Math.round((offset / size) * 100));
  toast.success(`Resumed from last chunk (${p}%)`);
}

export function tusFingerprintFor(fileFingerprint: string): string {
  return ['tus', fileFingerprint, 'drive'].join('-');
}

export class TusUpload implements UploadStrategy {
  constructor(private upload: Upload) {}

  start() {
    this.upload.start();
  }

  abort() {
    return this.upload.abort(false);
  }

  // Terminates the upload on the server (DELETE) and forgets the stored URL.
  async cancel() {
    try {
      await this.upload.abort(true);
    } catch {
      // Server may already have expired it; local refs are cleared below.
    }
  }

  static async create(
    file: UploadedFile,
    {
      onProgress,
      onSuccess,
      onError,
      uploadType,
      backendId,
      metadata,
      chunkSize,
      baseUrl,
    }: UploadStrategyConfigWithBackend,
  ): Promise<TusUpload> {
    const tusFingerprint = tusFingerprintFor(file.fingerprint);
    const upload = new Upload(file.native, {
      fingerprint: () => Promise.resolve(tusFingerprint),
      removeFingerprintOnSuccess: true,
      endpoint: `${baseUrl}/api/v1/tus/upload`,
      chunkSize,
      retryDelays: [0, 3000, 5000, 10000, 20000],
      overridePatchMethod: true,
      metadata: {
        name: window.btoa(file.id),
        clientName: file.name,
        clientExtension: file.extension,
        clientMime: file.mime || '',
        clientSize: `${file.size}`,
        uploadType: uploadType,
        backendId: backendId,
        ...(metadata as Record<string, string>),
      },
      headers: {
        'X-XSRF-TOKEN': getCookie('XSRF-TOKEN'),
      },
      onError: err => {
        if ('originalResponse' in err && err.originalResponse) {
          try {
            const message = JSON.parse(err.originalResponse.getBody())?.message;
            onError?.(message, file);
          } catch (e) {
            onError?.(null, file);
          }
        } else {
          onError?.(null, file);
        }
      },
      onProgress(bytesUploaded, bytesTotal) {
        onProgress?.({bytesUploaded, bytesTotal});
      },
      onSuccess: async () => {
        const uploadKey = upload.url?.split('/').pop();
        try {
          if (uploadKey) {
            const response = await storeTusFileEntry({uploadKey});
            onSuccess?.(response.data, file);
          }
        } catch (err) {
          localStorage.removeItem(tusFingerprint);
          const parsedError = parseApiError(err);
          onError?.(parsedError.message, file);
        }
      },
    });

    const previousUploads = await upload.findPreviousUploads();
    if (previousUploads[0]) {
      upload.resumeFromPreviousUpload(previousUploads[0]);
      const previous = previousUploads[0] as {
        sizeUploaded?: number;
        offset?: number;
      };
      toastResume(file.id, previous.sizeUploaded ?? previous.offset ?? 0, file.size);
    }

    return new TusUpload(upload);
  }
}
